/* -----------------UI--------------- */
import { useTranslation } from "react-i18next";
import { Button, Card, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { CardIconHeader } from "../Cards/CardIconHeader";
import { FlatCard } from "../Cards/FlatCard";
import DonutScore from "../DonutScore";
import TooltipWrapper from "../TooltipWrapper";
import { PracticeTestCardListItem } from "./PracticeTestCardListItem";
type ListItemData = {
	title: string;
	description: string;
	score: number;
	cta: string;
	numericId: number;
};

const MAX_VISIBLE_PRACTICE_TESTS = 3;

type Props = {
	title: string;
	subtitle: string;
	list: ListItemData[];
	avgScore: number;
	isWeb: boolean;
	appFont: string;
	handleNavigation: (numericId: number) => void;
	onViewMore?: () => void;
};

export const PracticeTestsCard = ({
	title,
	subtitle,
	list,
	avgScore,
	isWeb,
	appFont,
	handleNavigation,
	onViewMore,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const visibleList = onViewMore
		? list.slice(0, MAX_VISIBLE_PRACTICE_TESTS)
		: list;
	const hasMore = visibleList.length < list.length;
	const Content = () => (
		<Card.Content
			style={{
				paddingTop: 16,
				paddingBottom: 8,
				paddingHorizontal: 0,
				gap: 16,
			}}
		>
			<Box alignItems="center" gap={4}>
				<TooltipWrapper
					title={`Your current estimated performance score is ${avgScore}%. This is an approximation of how well you are expected to perform.`}
				>
					<DonutScore
						distribution={[avgScore, 100 - avgScore]}
						score={avgScore}
						radius={42}
						variant="progress"
						innerRadius={32}
						textVariant="titleMedium"
					/>
				</TooltipWrapper>
				<Text
					variant="bodySmall"
					style={{
						color: theme.colors.onSurfaceVariant,
						textAlign: "center",
					}}
				>
					Estimated Performance
				</Text>
			</Box>
			<Box>
				{visibleList.map(
					({ title: listItemTitle, description, score, cta, numericId }) => (
						<PracticeTestCardListItem
							key={listItemTitle}
							title={listItemTitle}
							description={description}
							score={score}
							cta={cta}
							numericId={numericId}
							appFont={appFont}
							isWeb={isWeb}
							handleNavigation={handleNavigation}
						/>
					),
				)}
			</Box>
		</Card.Content>
	);

	return (
		<FlatCard>
			<Box paddingHorizontal={16} paddingTop={16}>
				<CardIconHeader
					icon="clipboard-check-multiple-outline"
					title={title}
					subtitle={subtitle}
				/>
			</Box>
			<Content />
			{hasMore && onViewMore ? (
				<Card.Actions>
					<Button mode="text" onPress={onViewMore}>
						{t("dashboard.skills.progressCards.practiceTestsViewMore", {
							count: list.length,
						})}
					</Button>
				</Card.Actions>
			) : null}
		</FlatCard>
	);
};
